import { useMutation } from '@apollo/client';
import {
    faArrowUp,
    faChevronDown,
    faFolder,
    faGear,
    faPlus,
    faStop,
    faTriangleExclamation,
    faXmark,
} from '@fortawesome/pro-regular-svg-icons';
import * as React from 'react';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ProjectAgentBadge } from './ProjectAgentBadge.tsx';
import { AttachmentIcon, getToolIcon, getToolMetaKey, humanizeToolName } from './toolDisplay.tsx';
import Button from '../../core/components/Button.tsx';
import DropdownMenu from '../../core/components/DropdownMenu.tsx';
import SelectField from '../../core/components/Fields/SelectField.tsx';
import { withIcon } from '../../core/components/Icon.tsx';
import SearchField from '../../core/components/SearchField.tsx';
import Switch from '../../core/components/Switch.tsx';
import Tooltip from '../../core/components/Tooltip.tsx';
import classnames from '../../core/utils/classnames.tsx';
import { UPLOAD_CONVERSATION_ATTACHMENT } from '../queries.ts';

const SendIcon = withIcon(faArrowUp);
const StopIcon = withIcon(faStop);
const RemoveIcon = withIcon(faXmark);
const PlusIcon = withIcon(faPlus);
const GearIcon = withIcon(faGear);
const ProjectIcon = withIcon(faFolder);
const ChevronDownIcon = withIcon(faChevronDown);
const WarningIcon = withIcon(faTriangleExclamation);

const ATTACHMENT_ACCEPT = '.pdf,.csv,.eml,.xlsx,.xls,image/png,image/jpeg,image/gif,image/webp';
const COMPOSER_MAX_HEIGHT = 160;
const MAX_ATTACHMENT_SIZE_MB = 5;
const MAX_ATTACHMENT_SIZE = MAX_ATTACHMENT_SIZE_MB * 1024 * 1024;
const MAX_MESSAGE_LENGTH = 10000;

const PLACEHOLDER_REGEX = /\[[^\]]+]/g;

const findPlaceholderAt = (text: string, from: number, to: number) => {
    PLACEHOLDER_REGEX.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = PLACEHOLDER_REGEX.exec(text))) {
        const start = match.index;
        const end = start + match[0].length;
        if (from >= start && to <= end) return { start, end };
    }
    return null;
};

export interface ComposerHandle {
    fillPrompt: (text: string) => void;
}

export const Composer = forwardRef<ComposerHandle, any>(function Composer(
    {
        onSend,
        disabled,
        sendDisabled = false,
        project,
        projects = [],
        onSelectProject = null,
        onCreateProject = null,
        placeholder,
        conversationId = null,
        tools = [],
        selectedTools = null,
        onToggleTool = null,
        toolsDisabled = false,
        isStreaming = false,
        isStopping = false,
        onStop = null,
        models = [],
        selectedModelKey = null,
        onModelChange = null,
        decoration = null, // optional decoration around the input box (landing page: glow orb behind it, Worki sitting on its edge)
    },
    ref
) {
    const { t, i18n } = useTranslation('chat');
    const [value, setValue] = useState('');
    const [fileHints, setFileHints] = useState<string[]>([]);
    const [pendingAttachments, setPendingAttachments] = useState<any[]>([]);
    const [uploading, setUploading] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const dragDepthRef = useRef(0);
    const localIdRef = useRef(0);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [uploadAttachment] = useMutation(UPLOAD_CONVERSATION_ATTACHMENT);

    const autoResize = () => {
        const textarea = textareaRef.current;
        if (!textarea) return;
        textarea.style.height = 'auto';
        textarea.style.height = `${Math.min(textarea.scrollHeight, COMPOSER_MAX_HEIGHT)}px`;
    };

    const isAllowedFile = (name: string) =>
        ['.pdf', '.csv', '.eml', '.xlsx', '.xls', '.png', '.jpg', '.jpeg', '.gif', '.webp'].some((ext) =>
            name.toLowerCase().endsWith(ext)
        );

    const fileNames = (files: File[]) =>
        new Intl.ListFormat(i18n.language, { style: 'long', type: 'conjunction' }).format(
            files.map((file) => file.name)
        );

    const handleFiles = async (files: FileList | null) => {
        if (!files?.length) return;
        const all = Array.from(files);
        const unsupported = all.filter((file) => !isAllowedFile(file.name));
        const tooLarge = all.filter((file) => isAllowedFile(file.name) && file.size > MAX_ATTACHMENT_SIZE);
        const accepted = all.filter((file) => isAllowedFile(file.name) && file.size <= MAX_ATTACHMENT_SIZE);
        const skipped = [
            unsupported.length > 0 && t('composer.unsupportedFile', { files: fileNames(unsupported) }),
            tooLarge.length > 0 &&
                t('composer.fileTooLarge', { files: fileNames(tooLarge), maxSize: MAX_ATTACHMENT_SIZE_MB }),
        ].filter(Boolean);
        setFileHints(skipped);
        if (!accepted.length) return;

        if (!conversationId) {
            // no conversation yet — stage the files locally, they upload right after the conversation is created
            setPendingAttachments((prev) => [
                ...prev,
                ...accepted.map((file) => ({ id: `local-${localIdRef.current++}`, fileName: file.name, file })),
            ]);
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
        }

        setUploading(true);
        const failed: File[] = [];
        const unreadable: File[] = [];
        try {
            for (const file of accepted) {
                try {
                    const { data } = await uploadAttachment({ variables: { conversationId, file } });
                    const attachment = data.uploadAgentConversationAttachment.attachment;
                    if (!attachment.readable) unreadable.push(file);
                    setPendingAttachments((prev) => [...prev, attachment]);
                } catch (e) {
                    console.error(e);
                    failed.push(file);
                }
            }
            setFileHints([
                ...skipped,
                ...(failed.length ? [t('composer.uploadError', { files: fileNames(failed) })] : []),
                ...(unreadable.length
                    ? [t('composer.unreadableFile', { files: fileNames(unreadable), count: unreadable.length })]
                    : []),
            ]);
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const dropHandlers = {
        onDragEnter: (e: React.DragEvent) => {
            e.preventDefault();
            dragDepthRef.current += 1;
            setDragActive(true);
        },
        onDragOver: (e: React.DragEvent) => e.preventDefault(),
        onDragLeave: () => {
            dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
            if (dragDepthRef.current === 0) setDragActive(false);
        },
        onDrop: (e: React.DragEvent) => {
            e.preventDefault();
            dragDepthRef.current = 0;
            setDragActive(false);
            handleFiles(e.dataTransfer?.files || null);
        },
    };

    const renderPickerValue = (value, option) => (
        <span className="flex items-center gap-1.5">
            <span className="truncate">{option?.label || value}</span>
            <ChevronDownIcon className="flex-none text-content-weak" />
        </span>
    );

    // project selection is only offered before the conversation exists (the backend fixes the project at creation);
    // a project backed by an inbox agent is what "adding an agent" used to be (ENG-11385)
    const showProjectPicker = Boolean(onSelectProject);
    const selectedProject = showProjectPicker ? projects.find((candidate) => candidate.id === project) : null;
    // the picker lists plain projects first, then the ones an inbox assistant uses; a search narrows both
    const [projectSearch, setProjectSearch] = useState('');
    const visibleProjects = projects.filter((candidate) =>
        candidate.name.toLowerCase().includes(projectSearch.trim().toLowerCase())
    );
    const renderProjectItems = (items) =>
        items.map((candidate) => (
            <DropdownMenu.RadioItem key={candidate.id} value={candidate.id} className="gap-2">
                <ProjectIcon className="text-content-medium" />
                {candidate.name}
            </DropdownMenu.RadioItem>
        ));

    const showTools = tools.length > 0 && selectedTools && onToggleTool && Boolean(project);
    const toolLabel = (tool) => {
        const metaKey = getToolMetaKey(tool.name);
        return metaKey ? t(`new.tools.${metaKey}.label`) : humanizeToolName(tool.name);
    };
    const toolDescription = (tool) => {
        const metaKey = getToolMetaKey(tool.name);
        return metaKey ? t(`new.tools.${metaKey}.description`) : tool.description?.split('.')[0];
    };

    useImperativeHandle(ref, () => ({
        fillPrompt: (text: string) => {
            setValue(text);
            requestAnimationFrame(() => {
                const textarea = textareaRef.current;
                if (!textarea) return;
                textarea.focus();
                const placeholder = text.match(/\[[^\]]+]/);
                const start = placeholder ? placeholder.index : text.length;
                const end = placeholder ? placeholder.index + placeholder[0].length : text.length;
                textarea.setSelectionRange(start, end);
                autoResize();
            });
        },
    }));

    const tooLong = value.length > MAX_MESSAGE_LENGTH;
    const hints = [
        ...fileHints,
        ...(tooLong
            ? [t('composer.tooLong', { max: new Intl.NumberFormat(i18n.language).format(MAX_MESSAGE_LENGTH) })]
            : []),
    ];

    const handleSend = () => {
        const message = value.trim();
        if (!message || disabled || sendDisabled || tooLong || uploading || isStreaming) return;
        setValue('');
        setFileHints([]);
        const attachmentIds = pendingAttachments.filter((a) => !a.file).map((a) => a.id);
        const localFiles = pendingAttachments.filter((a) => a.file).map((a) => a.file);
        setPendingAttachments([]);
        onSend(message, attachmentIds, localFiles);
        if (textareaRef.current) textareaRef.current.style.height = 'auto';
    };

    return (
        <div className="mx-auto w-full max-w-3xl px-6 pb-2 pt-2">
            {hints.length > 0 && (
                <div className="mb-2 flex flex-col gap-1" role="status">
                    {hints.map((hint) => (
                        <div key={hint} className="flex items-start gap-1.5 text-xs text-content-medium">
                            <WarningIcon className="mt-0.5 flex-none text-content-warning-strong" />
                            <span>{hint}</span>
                        </div>
                    ))}
                </div>
            )}
            {pendingAttachments.length > 0 && (
                <div className="mb-2 flex flex-wrap gap-1.5">
                    {pendingAttachments.map((attachment) => (
                        <span
                            key={attachment.id}
                            className="flex items-center gap-1.5 rounded-full border border-solid border-line-strong bg-surface-weak px-2.5 py-1 text-xs font-medium text-content-medium"
                        >
                            <AttachmentIcon />
                            {attachment.fileName}
                            <button
                                className="cursor-pointer border-none bg-transparent p-0 text-content-weak hover:text-content-strong"
                                onClick={() =>
                                    setPendingAttachments((prev) => prev.filter((a) => a.id !== attachment.id))
                                }
                            >
                                <RemoveIcon />
                            </button>
                        </span>
                    ))}
                </div>
            )}
            <div
                className={classnames(
                    'group/composer relative flex flex-col gap-2 rounded-xl border border-solid bg-surface-weak px-3 py-2 shadow-sm transition-colors focus-within:border-line-hover',
                    dragActive ? 'border-line-brand-default bg-surface-brand-medium-default' : 'border-line-default'
                )}
                {...dropHandlers}
            >
                {decoration}
                {dragActive && (
                    <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-xl border border-dashed border-line-brand-default bg-surface-brand-medium-default/90 text-sm font-semibold text-content-brand-strong">
                        <AttachmentIcon className="mr-2" />
                        {t('composer.dropHint')}
                    </div>
                )}
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept={ATTACHMENT_ACCEPT}
                    className="hidden"
                    onChange={(e) => handleFiles(e.target.files)}
                />
                <textarea
                    ref={textareaRef}
                    rows={2}
                    name="chat"
                    autoFocus
                    className={classnames(
                        'max-h-40 w-full flex-1 resize-none overflow-y-auto border-none bg-transparent py-1.5 text-sm text-content-strong outline-none placeholder:text-content-weak selection:bg-surface-brand-weak selection:text-content-brand-default',
                        decoration && 'pr-18'
                    )}
                    placeholder={placeholder}
                    value={value}
                    disabled={disabled}
                    onChange={(e) => {
                        setValue(e.target.value);
                        autoResize();
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSend();
                        }
                    }}
                    onClick={(e) => {
                        const textarea = e.currentTarget;
                        const placeholder = findPlaceholderAt(
                            textarea.value,
                            textarea.selectionStart,
                            textarea.selectionEnd
                        );
                        if (!placeholder) return;
                        const nextValue =
                            textarea.value.slice(0, placeholder.start) + textarea.value.slice(placeholder.end);
                        setValue(nextValue);
                        requestAnimationFrame(() => {
                            textarea.setSelectionRange(placeholder.start, placeholder.start);
                            autoResize();
                        });
                    }}
                />
                <div className="flex items-center justify-between gap-2">
                    <DropdownMenu>
                        <DropdownMenu.Trigger asChild>
                            <Button variant="ghost" className="px-2.5" disabled={disabled} loading={uploading}>
                                <PlusIcon />
                            </Button>
                        </DropdownMenu.Trigger>
                        <DropdownMenu.Content align="start" side="top" className="min-w-56">
                            <DropdownMenu.Item onSelect={() => fileInputRef.current?.click()}>
                                <AttachmentIcon className="mr-2.5 text-content-medium" />
                                {t('composer.addAttachment')}
                            </DropdownMenu.Item>
                            {showTools && (
                                <DropdownMenu.Sub>
                                    <DropdownMenu.SubTrigger className="px-3 py-2" disabled={toolsDisabled}>
                                        <GearIcon className="mr-2.5 text-content-medium" />
                                        {t('composer.tools')}
                                        <span className="ml-auto mr-1.5 rounded bg-surface-brand-medium-default px-1.5 py-0.5 text-[11px] font-semibold text-content-brand-strong">
                                            {selectedTools.size}/{tools.length}
                                        </span>
                                    </DropdownMenu.SubTrigger>
                                    <DropdownMenu.SubContent className="max-h-80 min-w-64 overflow-y-auto p-1.5">
                                        {tools.map((tool) => {
                                            const ToolIcon = getToolIcon(tool.name);
                                            const selected = selectedTools.has(tool.name);
                                            return (
                                                <Tooltip key={tool.name} content={toolDescription(tool)} side="left">
                                                    <DropdownMenu.Item
                                                        className="gap-2.5"
                                                        onSelect={(e) => {
                                                            e.preventDefault();
                                                            onToggleTool(tool.name);
                                                        }}
                                                    >
                                                        <ToolIcon className="text-content-medium" />
                                                        <span className="text-sm text-content-strong">
                                                            {toolLabel(tool)}
                                                        </span>
                                                        <span className="pointer-events-none ml-auto">
                                                            <Switch value={selected} />
                                                        </span>
                                                    </DropdownMenu.Item>
                                                </Tooltip>
                                            );
                                        })}
                                    </DropdownMenu.SubContent>
                                </DropdownMenu.Sub>
                            )}
                        </DropdownMenu.Content>
                    </DropdownMenu>
                    {showProjectPicker &&
                        (selectedProject ? (
                            <span className="flex items-center gap-1.5 rounded-full bg-surface-brand-medium-default px-2.5 py-1 text-xs font-medium text-content-brand-strong">
                                <ProjectIcon />
                                {selectedProject.name}
                                <ProjectAgentBadge
                                    project={selectedProject}
                                    iconOnly
                                    className="text-content-brand-default"
                                />
                                <button
                                    className="cursor-pointer border-none bg-transparent p-0 text-content-brand-default hover:text-content-brand-strong"
                                    onClick={() => onSelectProject(null)}
                                    aria-label={t('composer.removeProject')}
                                >
                                    <RemoveIcon />
                                </button>
                            </span>
                        ) : (
                            <DropdownMenu
                                onOpenChange={(open) => {
                                    if (!open) setProjectSearch('');
                                }}
                            >
                                <DropdownMenu.Trigger asChild>
                                    <Button variant="ghost" className="px-2.5" disabled={disabled}>
                                        <ProjectIcon className="mr-2 text-content-medium" />
                                        {t('composer.project')}
                                        <ChevronDownIcon className="ml-2 text-xs text-content-weak" />
                                    </Button>
                                </DropdownMenu.Trigger>
                                <DropdownMenu.Content
                                    align="start"
                                    side="top"
                                    className="max-h-96 min-w-64 overflow-y-auto p-1.5"
                                >
                                    {/* typed characters belong to the search box, not the menu's typeahead; arrows,
                                        Escape and Enter still reach the menu */}
                                    <div
                                        className="px-1 pb-1.5"
                                        onKeyDown={(e) => {
                                            if (e.key.length === 1) e.stopPropagation();
                                        }}
                                    >
                                        <SearchField
                                            value={projectSearch}
                                            onValueChange={setProjectSearch}
                                            placeholder={t('composer.searchProjects')}
                                        />
                                    </div>
                                    {onCreateProject && (
                                        <DropdownMenu.Item onSelect={onCreateProject}>
                                            <PlusIcon className="mr-2.5 text-content-medium" />
                                            {t('composer.newProject')}
                                        </DropdownMenu.Item>
                                    )}
                                    {(onCreateProject || visibleProjects.length > 0) && <DropdownMenu.Separator />}
                                    <DropdownMenu.RadioGroup
                                        value={project || ''}
                                        onValueChange={(value) => onSelectProject(value || null)}
                                    >
                                        {renderProjectItems(visibleProjects.filter((candidate) => !candidate.agent))}
                                        {visibleProjects.some((candidate) => candidate.agent) && (
                                            <>
                                                <DropdownMenu.Label className="text-xs text-content-weak">
                                                    {t('composer.assistantsGroup')}
                                                </DropdownMenu.Label>
                                                {renderProjectItems(
                                                    visibleProjects.filter((candidate) => candidate.agent)
                                                )}
                                            </>
                                        )}
                                    </DropdownMenu.RadioGroup>
                                    {visibleProjects.length === 0 && projects.length > 0 && (
                                        <div className="px-3 py-2 text-sm text-content-weak">
                                            {t('composer.noProjectsFound')}
                                        </div>
                                    )}
                                </DropdownMenu.Content>
                            </DropdownMenu>
                        ))}
                    <div className="ml-auto flex items-center gap-2">
                        {models.length > 0 && (
                            <SelectField
                                className={classnames(
                                    'cursor-pointer bg-transparent! border-none! shadow-none! hover:bg-surface-strong! [&>div:last-child]:hidden'
                                )}
                                style={{ cursor: 'pointer' }}
                                required
                                options={models.map((model) => ({ value: model.key, label: model.label }))}
                                renderValue={renderPickerValue}
                                value={selectedModelKey || ''}
                                onValueChange={onModelChange}
                            />
                        )}
                        {isStreaming ? (
                            <Tooltip content={t('composer.stop')}>
                                <Button
                                    variant="primary"
                                    className="px-2.5"
                                    disabled={isStopping}
                                    onClick={onStop}
                                    aria-label={t('composer.stop')}
                                >
                                    <StopIcon />
                                </Button>
                            </Tooltip>
                        ) : (
                            <Button
                                variant="primary"
                                className="px-2.5"
                                disabled={disabled || sendDisabled || tooLong || uploading || !value.trim()}
                                onClick={handleSend}
                            >
                                <SendIcon />
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
});
